/**
 * ONE schedule read, for the SELECTED card only, and only for the three reasons whose answer is the
 * client's rhythm.
 *
 * Four weeks in one range, because `GET /calendarevent/customer/{id}/schedule` takes a window and
 * the strip and the attendance run are the same four weeks — a second read for the history would be
 * the same rows fetched twice.
 *
 * ── Why it is gated on the SELECTION and not on the deck ─────────────────────────────────────
 * Fourteen cards would be fourteen schedule reads on the first paint, thirteen of them for cards
 * the trainer may never look at. The desk shows one card, so one read is the honest number; moving
 * the selection with `↑ ↓` fetches the next one, and TanStack Query serves a card the trainer comes
 * back to from cache.
 *
 * `enabled` also refuses a null customer id: the pending invitation's invitee is not a client yet
 * (R10) and has no schedule to read.
 */

import { useQuery } from '@tanstack/react-query';

import { customerScheduleQueryOptions, scheduleWindow } from '~/entities/calendar-event';

import { clientWeek, clientWeekSpan, type ClientWeek, type ClientWeekEvent } from '../model/clientWeek';

export interface ClientWeekState {
  /** `null` while the read is open, or when this card draws no week at all. */
  readonly week: ClientWeek | null;
  /** The read is open. The strip reserves its box and gates its skeleton on this. */
  readonly pending: boolean;
  /** The schedule read failed — the week is missing, not empty. */
  readonly failed: boolean;
}

export interface ClientWeekQuery {
  /** The card's subject, or `null` where it has none. */
  readonly customerId: number | null;
  /** Whether this card's reason draws a week at all — `model/triageCard`'s `showsWeek`. */
  readonly wanted: boolean;
  readonly todayIso: string;
  /** The client's civil day the task is about, or `null`. */
  readonly taskDay: string | null;
}

export function useClientWeek({ customerId, wanted, todayIso, taskDay }: ClientWeekQuery): ClientWeekState {
  const span = clientWeekSpan(todayIso, taskDay);
  const enabled = wanted && customerId !== null;
  const schedule = useQuery(
    customerScheduleQueryOptions(customerId ?? 0, scheduleWindow(span.first, span.last), { enabled }),
  );

  if (!enabled) return { week: null, pending: false, failed: false };
  // A FAILED read is not an empty week: no honest-looking strip of seven rest days, and the strip
  // says the read failed in the box it held for it.
  if (schedule.isError) return { week: null, pending: false, failed: true };
  if (schedule.data === undefined) return { week: null, pending: true, failed: false };

  // The PLACEMENT fields travel whole: which square an occurrence lands in is R17's decision and
  // `entities/calendar-event` takes it. `displayDate` is a `DateTime` on the wire, never a day.
  const events: readonly ClientWeekEvent[] = schedule.data.map((event) => ({
    displayDate: event.displayDate,
    eventDate: event.eventDate,
    eventInstant: event.eventInstant,
    moment: event.moment,
    status: event.status,
    derivedStatus: event.derivedStatus ?? null,
    sessionName: event.sessionName ?? null,
  }));

  return { week: clientWeek(events, todayIso, taskDay), pending: false, failed: false };
}
